import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, forkJoin, Observable, of, switchMap, map } from 'rxjs';

import {
	AuthenticationInput,
	AuthenticationResponseSet,
	AuthenticationResultCode,
	DeviceAuthenticationCoreApiService
} from '@shure/shared/angular/data-access/device-authentication';
import {
	AuthenticationDevice,
	DeviceAuthenticationApiService
} from '@shure/shared/angular/features/device-authentication/data-access';
import { CloseTextOption, MessageType, SnackbarService } from '@shure/shared/angular/ui/components/html-snackbar';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@Injectable({ providedIn: 'root' })
@shUseTranslation('scDeviceAuth', (lang) => import(`../../../i18n/${lang}`))
export class DeviceAuthenticationService {
	constructor(
		private readonly authenticationCoreApiService: DeviceAuthenticationCoreApiService,
		private readonly authenticationApiService: DeviceAuthenticationApiService,
		private readonly snackbarService: SnackbarService,
		private readonly transloco: TranslocoService
	) {}

	public authenticationDevices$(deviceIds: string[]): Observable<AuthenticationDevice[]> {
		return this.authenticationApiService.getDevices$(deviceIds);
	}

	public setupPasswordForDevices(deviceIds: string[], password: string): Observable<boolean> {
		if (deviceIds.length === 0) {
			return of(true);
		}

		return this.authenticationCoreApiService
			.setupPasswordAndAuthenticate(deviceIds.map((deviceId) => <AuthenticationInput>{ deviceId, password }))
			.pipe(map((authResponse) => this.didMultiOperationSucceed([authResponse.response])));
	}

	public async setupWithoutAuthenticationForDevices(deviceIds: string[]): Promise<boolean> {
		if (deviceIds.length === 0) {
			return true;
		}
		const setupResult = await this.authenticationCoreApiService.setupWithoutAuthentication(deviceIds);
		if (setupResult.deviceResponses?.map((m) => m.response) !== undefined) {
			return this.didMultiOperationSucceed(setupResult.deviceResponses?.map((m) => m.response));
		}
		return false;
	}

	public async updatePasswordForDevices(deviceIds: string[], password: string): Promise<boolean> {
		if (deviceIds.length === 0) {
			return true;
		}

		const devices = await firstValueFrom(this.authenticationDevices$(deviceIds));

		const setupDevices = devices
			.filter((device) => !device.features.authentication?.enabled)
			.map((d) => {
				return { deviceId: d.id, password };
			});

		let setupResult: AuthenticationResponseSet | undefined;
		if (setupDevices.length > 0) {
			setupResult = await firstValueFrom(
				this.authenticationCoreApiService.setupPasswordAndAuthenticate(setupDevices)
			);
		}

		let updateResult: AuthenticationResultCode | undefined;
		const updateDevices = devices.filter((device) => device.features.authentication?.enabled).map((d) => d.id);
		if (updateDevices.length > 0) {
			updateResult = await firstValueFrom(
				this.authenticationCoreApiService.updatePassword(updateDevices, password)
			);
		}

		return this.didMultiOperationSucceed([
			setupResult?.response || AuthenticationResultCode.Ok,
			updateResult || AuthenticationResultCode.Ok
		]);
	}

	public removePasswordFromDevices(deviceIds: string[]): Observable<boolean> {
		if (deviceIds.length === 0) {
			return of(true);
		}

		return forkJoin(deviceIds.map((deviceId) => this.authenticationCoreApiService.removePassword(deviceId))).pipe(
			map((responses) => this.didMultiOperationSucceed(responses))
		);
	}
	public clearPasswordFromDevice(deviceId: string): Observable<boolean> {
		return this.authenticationCoreApiService
			.clearPassword(deviceId)
			.pipe(switchMap((result) => of(result === AuthenticationResultCode.Ok)));
	}

	public authenticate(deviceIds: string[], password: string): Observable<AuthenticationResponseSet> {
		const authenticationInput: AuthenticationInput[] = deviceIds.map((deviceId) => ({ deviceId, password }));

		return this.authenticationCoreApiService.authenticate(authenticationInput);
	}

	public verify(deviceIds: string[], password: string): Observable<AuthenticationResponseSet> {
		const verificationInput: AuthenticationInput[] = deviceIds.map((deviceId) => ({ deviceId, password }));

		return this.authenticationCoreApiService.verify(verificationInput);
	}

	private didMultiOperationSucceed(responses: AuthenticationResultCode[]): boolean {
		const isNotAuthenticated = responses.some((res) => res === AuthenticationResultCode.NotAuthenticated);
		const failedToUpdate = isNotAuthenticated || responses.some((res) => res !== AuthenticationResultCode.Ok);

		if (failedToUpdate) {
			this.snackbarService.open(
				isNotAuthenticated
					? this.transloco.translate(
							'scDeviceAuth.common-device-password.errors.not-all-devices-authenticated'
						)
					: this.transloco.translate('scDeviceAuth.common-device-password.errors.could-not-set-password'),
				CloseTextOption.Dismiss,
				MessageType.Error
			);
		}
		return !failedToUpdate;
	}
}
